import { Experience } from "../quiz/experience";
import { Goals } from "../quiz/goals";
import { Gender } from "../quiz/gender";
import { ActivityLevel } from "../quiz/activity";
import { BodyFat } from "../quiz/bodyfat";
import { Physiology } from "../quiz/physiology";
import { getGenderKey } from "./utils";
import { NumberWorkouts } from "../quiz/numberWorkouts";

type WCTMapping = {
  [gender in Exclude<Gender, Gender.NonBinary>]: {
    [experience in Experience]: {
      [goal in Goals]: {
        upper: number;
        lower: number;
        wcm: number;
      }[];
    };
  };
};

const wctMapping: WCTMapping = {
  [Gender.Male]: {
    [Experience.Beginner]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.2,
          wcm: -0.007,
        },
        {
          upper: 0.2,
          lower: 0.12,
          wcm: -0.005,
        },
        {
          upper: 0.12,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.00375,
        },
      ],
    },
    [Experience.Intermediate]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.15,
          wcm: -0.007,
        },
        {
          upper: 0.15,
          lower: 0.12,
          wcm: -0.005,
        },
        {
          upper: 0.12,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.0025,
        },
      ],
    },
    [Experience.Advanced]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.15,
          wcm: -0.007,
        },
        {
          upper: 0.15,
          lower: 0.12,
          wcm: -0.005,
        },
        {
          upper: 0.12,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.0025,
        },
      ],
    }
  },
  [Gender.Female]: {
    [Experience.Beginner]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.3,
          wcm: -0.007,
        },
        {
          upper: 0.3,
          lower: 0.22,
          wcm: -0.005,
        },
        {
          upper: 0.22,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.0025,
        },
      ],
    },
    [Experience.Intermediate]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.25,
          wcm: -0.007,
        },
        {
          upper: 0.25,
          lower: 0.22,
          wcm: -0.005,
        },
        {
          upper: 0.22,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.00125,
        },
      ],
    },
    [Experience.Advanced]: {
      [Goals.BuildMuscleAndLoseFat]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0,
        },
      ],
      [Goals.LoseFat]: [
        {
          upper: 1,
          lower: 0.25,
          wcm: -0.007,
        },
        {
          upper: 0.25,
          lower: 0.22,
          wcm: -0.005,
        },
        {
          upper: 0.22,
          lower: 0,
          wcm: -0.003,
        },
      ],
      [Goals.BuildMuscle]: [
        {
          upper: 1,
          lower: 0,
          wcm: 0.00125,
        },
      ],
    },
  },
};



const getWeightChangeMultiplier = (gender: Gender, experience: Experience, goal: Goals, bodyFat: number, physiology?: Physiology) => {
  const genderKey = getGenderKey(gender, physiology);

  const ranges = wctMapping[genderKey]?.[experience]?.[goal];
  if (!ranges) {
    return 0;
  }
  for (let range of ranges) {
    if (bodyFat < range.upper && bodyFat >= range.lower) {
      return range.wcm;
    }
  }
  return 0;
};

const getActivityMultiplier = (activityLevel: ActivityLevel) => {
  switch (activityLevel) {
    case ActivityLevel.Sedentary:
      return 1.25;
    case ActivityLevel.SomewhatActive:
      return 1.35;
    case ActivityLevel.VeryActive:
      return 1.5;
    case ActivityLevel.HardlySitStill:
      return 1.7;
  }
};

const getExtraSplitMultiplier = (numberWorkouts: NumberWorkouts) => {
  switch (numberWorkouts) {
    case NumberWorkouts.Two:
      return 0;
    case NumberWorkouts.Three:
      return 0.1;
    case NumberWorkouts.Four:
      return 0.15;
    case NumberWorkouts.Five:
      return 0.2;
    case NumberWorkouts.AsManyAsPossible:
      return 0.25;
  }
};

const calculateTdeeI = (lbs: number, bodyfat: BodyFat, activityLevel: ActivityLevel, numberWorkouts: NumberWorkouts): number => {
  const leanMass = lbs * (1 - bodyfat / 100);
  const activityMultiplier = getActivityMultiplier(activityLevel);
  const extraSplitMultiplier = getExtraSplitMultiplier(`${numberWorkouts}` as NumberWorkouts);

  const tdee = parseInt(((370 + 9.82 * leanMass) * (activityMultiplier + extraSplitMultiplier)).toPrecision(4));
  return tdee;
};

const calculateWeightChangeTarget = (wcm: number, weight: number) => {
  return wcm * weight;
};

const calTarget = (wcm: number, weightLbs: number, tdee: number, gender: Gender, physiology?: Physiology) => {
  const weightChangeTarget = calculateWeightChangeTarget(wcm, weightLbs);
  let calTarget = weightChangeTarget * 500 + tdee;
  return Math.max(gender === Gender.Male || physiology === Physiology.Male ? 1500 : 1200, Math.round(calTarget));
}

const calculateCalorieTarget = (experience: Experience, goal: Goals, bodyFat: BodyFat, gender: Gender, weightLbs: number, activityLevel: ActivityLevel, numberWorkouts: NumberWorkouts, physiology?: Physiology): number => {
  const tdee = calculateTdeeI(weightLbs, bodyFat, activityLevel, numberWorkouts);
  const wcm = getWeightChangeMultiplier(gender, experience, goal, bodyFat / 100);
  return calTarget(wcm, weightLbs, tdee, gender, physiology);
};

// Old protein calculation
const calculateProteinTarget = (gender: Gender, bodyFat: BodyFat, lbs: number) => {
  const bodyFatRate = bodyFat / 100;
  let proteinMultiplier = 1;
  if (gender === Gender.Male) {
    if (bodyFatRate > 0.25) {
      proteinMultiplier = 0.73;
    } else if (bodyFatRate >= 0.2) {
      proteinMultiplier = 0.8;
    }
  } else {
    if (bodyFatRate > 0.25) {
      proteinMultiplier = 0.8;
    }
  }
  return Math.round(proteinMultiplier * lbs);
};

// Updated protein range calculation (2025)
// See: https://docs.google.com/document/d/1nUJ0F4n6XtU6AM1JHu2cQS6lQlSGZfwtTboCg6i1Uo4/edit?tab=t.0
const calculateProteinRange = (
  bodyfat: number,
  lbs: number
): [number, number] => {
  const bodyFatRate = bodyfat / 100;
  const weightkg = lbs / 2.2;
  const ffm = weightkg * (1 - bodyFatRate);

  const prominadj = Math.round(ffm >= 90 ? 148.5 : 1.65 * ffm);
  const promaxadj = Math.round(ffm >= 90 ? 252 : 2.8 * ffm);

  return [prominadj, promaxadj];
};

export {
  calculateProteinRange,
  getWeightChangeMultiplier,
  calculateCalorieTarget,
  calculateProteinTarget,
  calculateWeightChangeTarget,
  calculateTdeeI,
};